import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API_BASE_URL } from "./config";

const FollowRequests = () => {
  const [receivedRequests, setReceivedRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("received");

  useEffect(() => {
    fetchFollowRequests();
  }, []);

  const fetchFollowRequests = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE_URL}/api/follow-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setReceivedRequests(res.data.received || []);
      setSentRequests(res.data.sent || []);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching follow requests:", err);
      setLoading(false);
    }
  };

  const handleAccept = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/api/follow-requests/${userId}/accept`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Remove from list
      setReceivedRequests(receivedRequests.filter(req => req._id !== userId));
    } catch (err) {
      alert(err.response?.data?.error || "Error accepting request");
    }
  };

  const handleReject = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/api/follow-requests/${userId}/reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Remove from list
      setReceivedRequests(receivedRequests.filter(req => req._id !== userId));
    } catch (err) {
      alert(err.response?.data?.error || "Error rejecting request");
    }
  };

  const handleCancel = async (username) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/api/users/${username}/cancel-follow-request`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Remove from list
      setSentRequests(sentRequests.filter(req => req.displayUsername !== username));
    } catch (err) {
      alert(err.response?.data?.error || "Error cancelling request");
    }
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <ul className="nav nav-tabs card-header-tabs">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "received" ? "active" : ""}`}
              onClick={() => setActiveTab("received")}
            >
              Received ({receivedRequests.length})
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "sent" ? "active" : ""}`}
              onClick={() => setActiveTab("sent")}
            >
              Sent ({sentRequests.length})
            </button>
          </li>
        </ul>
      </div>
      <div className="card-body">
        {activeTab === "received" ? (
          <>
            <h5>Follow Requests</h5>
            {receivedRequests.length === 0 ? (
              <p className="text-muted">No pending follow requests</p>
            ) : (
              <div className="list-group">
                {receivedRequests.map(request => (
                  <div key={request._id} className="list-group-item">
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        {request.profilePic ? (
                          <img
                            src={`${API_BASE_URL}/${request.profilePic}`}
                            alt={request.fullName || request.displayUsername}
                            className="rounded-circle me-3"
                            style={{ width: "50px", height: "50px", objectFit: "cover" }}
                          />
                        ) : (
                          <div 
                            className="rounded-circle me-3 bg-secondary d-flex align-items-center justify-content-center"
                            style={{ width: "50px", height: "50px" }}
                          >
                            <span className="text-white">
                              {(request.fullName || request.displayUsername || "U")[0].toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div>
                          <Link to={`/user/${request.displayUsername}`} className="text-decoration-none">
                            <h6 className="mb-0">{request.fullName || request.displayUsername}</h6>
                            {request.displayUsername && (
                              <small className="text-muted">@{request.displayUsername}</small>
                            )}
                          </Link>
                          {request.tagline && (
                            <p className="mb-0 small text-muted">{request.tagline}</p>
                          )}
                          {request.city && (
                            <p className="mb-0 small text-muted">📍 {request.city}</p>
                          )}
                        </div>
                      </div>
                      <div>
                        <button
                          className="btn btn-sm btn-success me-2"
                          onClick={() => handleAccept(request._id)}
                        >
                          Accept
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleReject(request._id)}
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <h5>Sent Requests</h5>
            {sentRequests.length === 0 ? (
              <p className="text-muted">No pending requests sent</p>
            ) : (
              <div className="list-group">
                {sentRequests.map(request => (
                  <div key={request._id} className="list-group-item">
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        {request.profilePic ? (
                          <img
                            src={`${API_BASE_URL}/${request.profilePic}`}
                            alt={request.fullName || request.displayUsername}
                            className="rounded-circle me-3"
                            style={{ width: "50px", height: "50px", objectFit: "cover" }}
                          />
                        ) : (
                          <div 
                            className="rounded-circle me-3 bg-secondary d-flex align-items-center justify-content-center"
                            style={{ width: "50px", height: "50px" }}
                          >
                            <span className="text-white">
                              {(request.fullName || request.displayUsername || "U")[0].toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div>
                          <Link to={`/user/${request.displayUsername}`} className="text-decoration-none">
                            <h6 className="mb-0">{request.fullName || request.displayUsername}</h6>
                            {request.displayUsername && (
                              <small className="text-muted">@{request.displayUsername}</small>
                            )}
                          </Link>
                        </div>
                      </div>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => handleCancel(request.displayUsername)}
                      >
                        Cancel Request
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FollowRequests;